/**
 * A busca e a seleção múltipla na tela de macros.
 *
 * Pedido da Gisele (19/10/2026): "macros -- trazer a lista com busca e
 * seleção múltipla para ativar/desativar". A tela mostrava todas as macros
 * como cartões soltos, sem busca: com vinte macros, achar uma era varrer a
 * tela com os olhos, e desabilitar cinco era abrir cinco vezes o formulário
 * de edição.
 */

const normalizar = (valor) => String(valor ?? '')
    .trim()
    .toLowerCase()
    // Sem acento dos dois lados: ninguém digita "lembrança" com o acento certo
    // quando está com pressa, e a lista não pode fingir que a macro não existe.
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/**
 * A macro casa o texto procurado?
 *
 * Procura no NOME e nas PALAVRAS-CHAVE. As palavras-chave entram porque são o
 * que o operador associa à macro no dia a dia (é por elas que ela é sugerida
 * na caixa de ação) -- procurar "boleto" tem de achar a macro chamada
 * "2ª via financeiro" se "boleto" é palavra-chave dela.
 *
 * Busca vazia aceita tudo: caixa em branco não é filtro.
 */
export const macroCasaABusca = (macro, busca) => {
    const procurado = normalizar(busca);

    if (procurado === '') return true;
    if (!macro) return false;

    if (normalizar(macro.name).includes(procurado)) return true;

    return (Array.isArray(macro.keywords) ? macro.keywords : [])
        .some((k) => normalizar(k).includes(procurado));
};

/** As macros que casam a busca, na ordem em que chegaram. */
export const filtrarMacros = (macros, busca) =>
    (Array.isArray(macros) ? macros : []).filter((m) => macroCasaABusca(m, busca));

/**
 * Marca ou desmarca um id na seleção.
 *
 * Devolve SEMPRE uma lista nova, nunca a mesma mexida por dentro: a seleção é
 * estado de React, e mutar no lugar não redesenha a tela.
 */
export const alternarSelecao = (selecionados, id) => {
    const atuais = Array.isArray(selecionados) ? selecionados : [];

    return atuais.some((s) => String(s) === String(id))
        ? atuais.filter((s) => String(s) !== String(id))
        : [...atuais, id];
};

/**
 * A seleção limitada ao que está em tela.
 *
 * Quem marca três macros, digita uma busca e clica em "Desativar" espera
 * desativar o que ESTÁ VENDO. Sem este recorte, a ação atingiria macros que a
 * busca escondeu -- mudança invisível, que é o pior tipo.
 */
export const selecaoVisivel = (selecionados, macrosVisiveis) => {
    const visiveis = new Set(
        (Array.isArray(macrosVisiveis) ? macrosVisiveis : []).map((m) => String(m?.id))
    );

    return (Array.isArray(selecionados) ? selecionados : []).filter((id) => visiveis.has(String(id)));
};

/**
 * O resumo de uma ação em lote, para a mensagem ao operador.
 *
 * Uma macro de outro operador pode recusar a mudança (403 do servidor: só o
 * criador ou quem tem permissão de gerenciar macros muda a dos outros).
 * Dizer "pronto" nesse caso seria mentir, e dizer só "deu erro" esconderia as
 * que funcionaram.
 *
 * @param {Array<{ok: boolean}>} resultados
 */
export const resumoDoLote = (resultados) => {
    const lista = Array.isArray(resultados) ? resultados : [];
    const ok = lista.filter((r) => r && r.ok).length;

    return { ok, falhas: lista.length - ok, total: lista.length };
};

/** A frase do resultado do lote, já pronta para o aviso da tela. */
export const mensagemDoLote = ({ ok, falhas }, ativando) => {
    const verbo = ativando ? 'ativada' : 'desativada';
    const plural = ok === 1 ? '' : 's';

    if (falhas === 0) return `${ok} macro${plural} ${verbo}${plural}.`;
    if (ok === 0) return `Nenhuma macro foi ${verbo}. Você pode não ter permissão sobre elas.`;

    return `${ok} macro${plural} ${verbo}${plural}; ${falhas} não foi possível (sem permissão).`;
};
